import PerText from '../../../components/custom_components/Text'
import { useTranslation } from 'react-i18next'
import { Divider, Table } from 'antd'
import { FC } from 'react'
import { SalaryDto } from '../../../types'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { useDateTime } from '../../../shared/useDateTime'
import { SortOrder } from 'antd/lib/table'
import useCompleteSalaryHistory from '../../../graphql/contractHistory/hooks/useCompleteSalaryHistory'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import { TimeZoneAndLocale } from '../../../types.custom'
import { round } from '../../../shared/numberUtil'

interface SalaryHistoryTableProps {
  employeeId: string
  isMonthlySalaryDisplayed: boolean
  monthlySalaryMonthsInYear: number
  noDivider?: boolean
  resigned?: boolean
  timezone: TimeZoneAndLocale
}

export const SalaryHistoryTable: FC<SalaryHistoryTableProps> = ({
  employeeId,
  isMonthlySalaryDisplayed,
  monthlySalaryMonthsInYear,
  noDivider = false,
  timezone,
  resigned,
}) => {
  const { formatDateShort } = useDateTime(timezone)
  const { t } = useTranslation()
  const { formatCurrency } = useAmountAndCurrency(timezone.locale)

  const { data, loading, error } = useCompleteSalaryHistory(employeeId, resigned)

  if (loading || error || !data) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data} error={error} />
  }

  const salaryList: SalaryDto[] = data.salaryByEmployeeId.salaryHistory
  const showSalaryData = !!salaryList && salaryList.length > 0

  const salaryColumns = [
    {
      key: 'salaryHistory-amount',
      title: t('profile.contract.salary.amount'),
      className: 'minWidth-column-m textAlign-right',
      sorter: (salaryA: SalaryDto, salaryB: SalaryDto) => {
        if (salaryA.amount && salaryB.amount) {
          return salaryA.amount - salaryB.amount
        }
        return 0
      },
      render: (salary: SalaryDto) => (
        <PerText>
          {formatCurrency(salary.amount, salary.currency)}
          <br />
          {isMonthlySalaryDisplayed && (
            <PerText fontSize="s" fontWeight="thin" color="secondary">
              {t('profile.contract.salary.monthlySalary', {
                salary: formatCurrency(round(salary.amount / monthlySalaryMonthsInYear, 2), salary.currency),
              })}
            </PerText>
          )}
          {salary.isFulltime ? (
            <PerText appearance="label-small">{t('profile.contract.salary.fulltimeSalary')}</PerText>
          ) : (
            <PerText appearance="label-small">{t('profile.contract.salary.actual')}</PerText>
          )}
        </PerText>
      ),
    },
    {
      key: 'salaryHistory-placeholder',
      className: 'minWidth-column-xxs',
    },
    {
      key: 'salaryHistory-percentageOfWorktime',
      title: t('profile.contract.salary.workingPercentage'),
      className: 'minWidth-column-m',
      sorter: (percentageA: SalaryDto, percentageB: SalaryDto) => {
        const a = percentageA.percentageOfWorktime ? percentageA.percentageOfWorktime : 0
        const b = percentageB.percentageOfWorktime ? percentageB.percentageOfWorktime : 0
        return a - b
      },
      render: (row: SalaryDto) => <PerText>{row.percentageOfWorktime * 100} %</PerText>,
    },
    {
      key: 'salaryHistory-fromDate',
      title: t('profile.contract.salary.fromDate'),
      className: 'minWidth-column-s',
      dataIndex: 'fromDate',
      defaultSortOrder: 'descend' as SortOrder,
      sorter: (dateA: SalaryDto, dateB: SalaryDto) => {
        const a = dateA.fromDate ? dateA.fromDate : ' '
        const b = dateB.fromDate ? dateB.fromDate : ' '
        return a.localeCompare(b)
      },
      render: (dataIndex: string) => <PerText>{dataIndex && formatDateShort(dataIndex)}</PerText>,
    },
    {
      key: 'salaryHistory-toDate',
      title: t('profile.contract.salary.toDate'),
      className: 'minWidth-column-s',
      dataIndex: 'toDate',
      sorter: (dateA: SalaryDto, dateB: SalaryDto) => {
        const a = dateA.toDate ? dateA.toDate : ' '
        const b = dateB.toDate ? dateB.toDate : ' '
        return a.localeCompare(b)
      },
      render: (dataIndex: string) => <PerText>{dataIndex && formatDateShort(dataIndex)}</PerText>,
    },
    {
      key: 'salaryHistory-note',
      title: t('profile.contract.salary.note'),
      className: 'minWidth-column-xl',
      render: (row: SalaryDto) => (
        <>
          {row.isTentative && <PerText>{t('profile.contract.salary.isTentative')}</PerText>}
          {row.isTentative && row.note && <br />}
          {row.note && <PerText>{row.note}</PerText>}
        </>
      ),
    },
  ]

  return (
    <>
      {showSalaryData && (
        <>
          {!noDivider && <Divider />}
          <PerText appearance="tile-heading" mb="s">
            {t('profile.contract.salary.header')}
          </PerText>
          <Table className="small" dataSource={salaryList} rowKey="id" columns={salaryColumns} pagination={false} />
        </>
      )}
    </>
  )
}
